import { ReactNode, useState, useCallback, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { useMutation } from "@tanstack/react-query";
import dayjs from "dayjs";
import { Stack } from "@altimateai/lego";
import DataTable from "@/components/tables/DataTable";
import { Paginate } from "@/components/paginate";
import { useTablePagination } from "@/components/tables/hooks/useTablePagination";
import { useDatatableContext } from "@/components/tables/contexts/DatatableContextProvider";
import { FilterApplicableFor } from "@/components/tags/types";
import { useAppState } from "@/modules/app/useAppContext";
import { getOpportunitiesRedirection } from "@/helpers/apis";
import { getResourceOpportunityUrl } from "@/pages/query/components";
import { useUsers } from "@/helpers/hooks";
import { User } from "@/types";
import type {
  GetOpportunitiesDataResponse,
  Page,
  OwnerListItem,
  SelectedRow,
} from "../../../types";
import { OpportunitiesBulkActions } from "../../../components/opportunities/OpportunitiesBulkActions";
import { PRIORITY_OPPORTUNITY_PREFIX } from "../../../constants";
import { useOpportunityNamesQuery } from "../../../hooks";
import { useOpportunitiesTableHeaders } from "./useOpportunitiesTableHeaders";

interface OpportunitiesListWithFilterProps {
  data: Page<GetOpportunitiesDataResponse> | undefined;
  isLoading: boolean;
  // The Implemented Opportunities tab reuses this component but reads
  // from a single-status table — passing true here drops the Status
  // column's filter dropdown so the user isn't offered a no-op filter.
  hideStatusFilter?: boolean;
  // Rendered in place of the table body once the fetch settles with zero
  // rows. The DataTable itself stays mounted (just visually hidden) so
  // the More-Filters / Saved-Filters portal children survive — without
  // that, applying a filter that returns no results would unmount the
  // filter UI and trap the user.
  emptyState?: ReactNode;
}

const OpportunitiesListWithFilter = ({
  data,
  isLoading,
  hideStatusFilter = false,
  emptyState,
}: OpportunitiesListWithFilterProps) => {
  const navigate = useNavigate();
  const { datastores = [] } = useAppState();
  const { getFilterValue } = useDatatableContext();
  // Note: ``useRegisterOpportunityFilters`` is mounted once at the page
  // level in ``DiscoverContentV2`` so the chip set is addressable from
  // every tab (Priority + Groups too), not only the tabs this component
  // renders on.
  const { page, pageSize, setPage, setPageSize, resetPage } = useTablePagination();
  const { users } = useUsers(false, true);

  // Get dates from context (same pattern as WarehouseListWithFilter)
  const startDateValue = getFilterValue<string>("startDate");
  const endDateValue = getFilterValue<string>("endDate");

  const startDate = useMemo(() => {
    if (!startDateValue) return dayjs().subtract(30, "day").toDate();
    return new Date(startDateValue);
  }, [startDateValue]);

  const endDate = useMemo(() => {
    if (!endDateValue) return dayjs().toDate();
    return new Date(endDateValue);
  }, [endDateValue]);

  const activeUserList = useMemo(() => {
    if (!users) return [{ label: "Unassigned", value: "-1" }];
    const userOptions = (users as User[])
      .filter((user: User) => user.first_name || user.last_name)
      .filter((user: User) => !user.deleted_at)
      .map((user: User) => ({
        label: [user.first_name, user.last_name].filter(Boolean).join(" "),
        value: String(user.id),
      }));
    return [{ label: "Unassigned", value: "-1" }, ...userOptions];
  }, [users]);

  // The shared `useOpportunityNamesQuery` already reads `?workspaceId=`
  // from the URL, includes it in the query key, and applies the same
  // ``frequent_short_warehouse_activity`` label rename. Reuse it so the
  // URL→typeahead wiring lives in exactly one place.
  //
  // Wrap the fallback empty-array in ``useMemo`` keyed on
  // ``opportunityNames`` identity so the prop passed downstream
  // (``useOpportunitiesTableHeaders`` depends on it) doesn't churn on
  // every render — otherwise the table headers' ``useMemo`` would
  // re-build all column definitions on every parent render.
  const { opportunityNames } = useOpportunityNamesQuery();
  const opportunityNameOptions = useMemo(() => opportunityNames ?? [], [opportunityNames]);

  const [selectedRows, setSelectedRows] = useState<SelectedRow[]>([]);
  const [ownerOverrides, setOwnerOverrides] = useState<Record<string, string>>({});

  const ownerList = useMemo<OwnerListItem[] | null>(() => {
    if (!data?.items?.length) return null;
    return data.items.map(item => ({
      opportunity_id: item.opportunity_id,
      owner: ownerOverrides[item.opportunity_id] ?? item.owner ?? "-1",
    }));
  }, [data?.items, ownerOverrides]);

  const { mutate } = useMutation({
    mutationFn: getOpportunitiesRedirection,
    onSuccess: (redirectData, variables) => {
      if (!redirectData?.url) return;
      navigate(
        getResourceOpportunityUrl({
          resource_type: variables.resource_type,
          opportunity_id: variables.resource_id ?? "",
          url: redirectData.url,
          startDate,
          endDate,
        })
      );
    },
  });

  const navigateToOpportunity = (row: GetOpportunitiesDataResponse) => {
    // Curated priority opportunities are namespaced under "priority/" — the
    // same rows the backend surfaces in the Priority tab (metadata.label =
    // "priority"). They aren't tied to a concrete resource, so — like the
    // Priority tab's click-through — they open the intermediary opportunity
    // detail page. Everything else resolves to its resource page via
    // `/redirection`.
    if (row.opportunity_id.startsWith(PRIORITY_OPPORTUNITY_PREFIX)) {
      if (row.instance_id == null) return;
      navigate(
        `/discover/opportunity/detail/${encodeURIComponent(row.opportunity_id)}?instance_id=${row.instance_id}`
      );
      return;
    }
    mutate({
      resource_type: row.resource_type,
      resource_uri: row.resources?.[0]?.resource_uri || null,
      resource_id: row.opportunity_id,
      resource_name: row.resource_name || null,
      instance_id: row.instance_id?.toString() || "",
      opportunity_id: row.opportunity_id,
    });
  };

  const handleRowSelect = useCallback((rows: GetOpportunitiesDataResponse[]) => {
    setSelectedRows(rows.map(row => ({ opportunity_id: row.opportunity_id })));
  }, []);

  const handleOwnerChange = useCallback((owner: string, opportunity_id: string) => {
    setOwnerOverrides(prev => ({ ...prev, [opportunity_id]: owner }));
  }, []);

  const handleBulkApplySuccess = useCallback((newOwnerList: OwnerListItem[]) => {
    const overrides: Record<string, string> = {};
    newOwnerList.forEach(item => {
      overrides[item.opportunity_id] = String(item.owner ?? "-1");
    });
    setOwnerOverrides(overrides);
    setSelectedRows([]);
  }, []);

  const handleCancelSelection = useCallback(() => {
    setSelectedRows([]);
  }, []);

  const instanceOptions = useMemo(
    () =>
      (datastores || [])
        .filter(d => d.instance_id != null)
        .map(d => ({
          label: d.name,
          value: String(d.instance_id),
        })),
    [datastores]
  );

  const headers = useOpportunitiesTableHeaders({
    activeUserList,
    ownerList,
    onOwnerChange: handleOwnerChange,
    startDate,
    endDate,
    opportunityNameOptions,
    instanceOptions,
    datastores,
    hideStatusFilter,
  });

  const showEmptyState = !isLoading && !data?.items?.length && Boolean(emptyState);

  return (
    <Stack direction="column" gap="lg" align="start" className="al-w-full">
      <OpportunitiesBulkActions
        visible={selectedRows.length > 0}
        selectedRows={selectedRows}
        ownerList={ownerList}
        onApplySuccess={handleBulkApplySuccess}
        onCancel={handleCancelSelection}
      />
      <div className="al-w-full">
        {/* The DataTable stays mounted on the empty path so its filter /
            saved-filter portal children survive. `al-hidden` only hides
            the table+pagination visually; the portals render into
            separately-mounted containers outside this subtree. */}
        <div className={showEmptyState ? "al-hidden" : undefined}>
          <DataTable<GetOpportunitiesDataResponse>
            enableRowSelection
            onRowSelect={handleRowSelect}
            onRowClick={row => navigateToOpportunity(row)}
            header={headers}
            data={{ items: data?.items || [] }}
            applicableFor={FilterApplicableFor.DISCOVER}
            columnPickerPortal="discover-columnpicker-container"
            columnPickerProps={{ buttonProps: { variant: "outline" } }}
            portals={{
              filter: "discover-filter-container",
              savedFilters: "discover-saved-filters-container",
            }}
            resetPage={resetPage}
            isLoading={isLoading}
          />
          <Paginate
            itemCount={data?.total || 0}
            numPages={data?.pages || 1}
            page={page}
            pageSize={pageSize}
            onPageClick={setPage}
            onPageSizeChange={setPageSize}
            showPageSize
            isLoading={isLoading}
          />
        </div>
        {showEmptyState && emptyState}
      </div>
    </Stack>
  );
};

export default OpportunitiesListWithFilter;
